// Los vendedores que aportaron a una regla, tienda o producto en el corte en curso.
import { CopyableId, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMoneyCents } from '@/shared/lib/format/money';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import type { Contributor } from '../../lib/paidRows';

interface ContributorsListProps {
    contributors: readonly Contributor[];
    directory: Pick<CommissionDirectory, 'sellerName' | 'isStoreManager'>;
    label: string;
    empty: string;
    limit?: number;
    /** El corte vivo todavía carga. */
    isLoading: boolean;
    /** El corte vivo no se pudo leer. */
    isError: boolean;
    /** Qué tanto cuadra la lista con la fila. */
    note: string | null;
    /** Qué cuenta `unitsCount`: piezas, o ventas en una regla que cuenta por venta. */
    unitLabel?: string;
}

export function ContributorsList({
    contributors,
    directory,
    label,
    empty,
    limit,
    isLoading,
    isError,
    note,
    unitLabel = 'pzas',
}: ContributorsListProps) {
    const shown = limit === undefined ? contributors : contributors.slice(0, limit);
    return (
        <>
            {note && <p className="mb-2 text-[10.5px] text-muted-foreground">{note}</p>}
            {isLoading ? (
                <Skeleton lines={3} />
            ) : isError ? (
                <p className="text-[11px] text-destructive">No se pudo leer el corte en curso.</p>
            ) : shown.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">{empty}</p>
            ) : (
                <Ranking shown={shown} directory={directory} label={label} unitLabel={unitLabel} />
            )}
        </>
    );
}

function Ranking({
    shown,
    directory,
    label,
    unitLabel,
}: {
    shown: readonly Contributor[];
    directory: ContributorsListProps['directory'];
    label: string;
    unitLabel: string;
}) {
    return (
        <ol className="flex flex-col gap-1" aria-label={label}>
            {shown.map((contributor, index) => (
                <li key={contributor.sellerId} className="flex items-center gap-2 text-[11px]">
                    <span
                        className={cn(
                            'w-5 text-right font-semibold',
                            index === 0 && 'text-warning',
                        )}
                    >
                        {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate">
                            {directory.sellerName(contributor.sellerId)}
                            {directory.isStoreManager(contributor.sellerId) ? ' ★' : ''}
                        </span>
                        <CopyableId
                            inline
                            label="ID"
                            value={contributor.sellerId}
                            className="text-[10px] text-muted-foreground"
                        />
                    </span>
                    <span className="text-muted-foreground">
                        {contributor.unitsCount} {unitLabel}
                    </span>
                    <span className="w-[84px] text-right font-semibold tabular-nums">
                        {formatMoneyCents(contributor.paidCents)}
                    </span>
                </li>
            ))}
        </ol>
    );
}
